import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import { useState, useEffect } from 'react';

export default function WolfiePage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigateToAbout = () => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/');
      setTimeout(() => {
        const aboutSection = document.getElementById('about');
        if (aboutSection) {
          aboutSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-100 via-blue-50 to-white">
      <Navigation scrolled={scrolled} />
      
      <div className="pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          {/* Back button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={navigateToAbout}
            className="flex items-center gap-2 text-gray-600 hover:text-blue-900 transition-colors mb-8 group"
          >
            <i className="ri-arrow-left-line text-2xl group-hover:-translate-x-1 transition-transform"></i>
            <span className="font-semibold">Back to Home</span>
          </motion.button>

          <div className="relative">
            {/* Profile Image - Float Left */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="float-left mr-8 mb-6 relative"
            >
              <div className="w-[320px] h-[400px] rounded-[48px] overflow-hidden border-8 border-blue-900 shadow-2xl">
                <img
                  src="https://static.readdy.ai/image/631821f386b34ef1da569fb8b8560804/672f8ec3bd6d0cf5722a03a6d9cb4bcb.png"
                  alt="Wolfie"
                  className="w-full h-full object-cover object-[center_30%]"
                />
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-blue-400 rounded-full opacity-20 blur-2xl"></div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-sky-400 rounded-full opacity-20 blur-2xl"></div>
            </motion.div>

            {/* Profile Info - Wrapping Text */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              <div>
                <h1 className="text-6xl font-black text-blue-900 mb-2" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                  Wolfie
                </h1>
                <p className="text-2xl font-bold text-gray-700">Co-Founder</p>
              </div>

              <div className="space-y-6 text-xl text-gray-700">
                <p className="leading-relaxed">
                  Hey there! I'm Wolfie, one of the co-founders of <strong className="text-blue-900">Can-do Crew</strong>. You may recognize me from my lemonade stand at the Farmer's Market. I'm starting "Can-Do Crew" with the money I made doing the lemonade stand these last three years. All my equipment, uniforms, materials, and operating costs are paid for by the work I've done and the money we earn serving our customers! I believe that even kids can make a big difference in their community!
                </p>

                <p className="leading-relaxed">
                  When I'm not cleaning trash cans, you can find me playing in my yard, playing with friends, playing soccer, or reading a bunch! I wanted to start this business because it seemed like a fun way to earn money!
                </p>

                <p className="leading-relaxed">
                  My favorite part of running my own business is interacting with my customers. 
                </p>
              </div>

              {/* Skills */}
              <div className="flex flex-wrap gap-3 clear-left">
                <span className="px-4 py-2 bg-blue-100 text-blue-900 rounded-full font-semibold text-sm">Energetic</span>
                <span className="px-4 py-2 bg-sky-100 text-sky-700 rounded-full font-semibold text-sm">Team Player</span>
                <span className="px-4 py-2 bg-yellow-100 text-yellow-600 rounded-full font-semibold text-sm">Friendly</span>
                <span className="px-4 py-2 bg-green-100 text-green-600 rounded-full font-semibold text-sm">Hard Worker</span>
              </div>

              {/* Meet Ruby Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/ruby')}
                className="w-full bg-gradient-to-r from-blue-900 to-blue-700 text-white px-8 py-4 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-heart-fill text-2xl"></i>
                Meet My Partner Ruby!
              </motion.button>
            </motion.div>
          </div>

          {/* Two-column boxes section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="grid md:grid-cols-2 gap-8 mt-12"
          >
            {/* Fiscal Responsibility Box */}
            <div className="bg-white rounded-3xl p-8 shadow-lg border-4 border-blue-200">
              <h3 className="text-3xl font-bold text-gray-800 mb-4" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                We are learning fiscal responsibility
              </h3>
              <p className="text-xl font-semibold text-gray-700 mb-6">With the income we earn, we put:</p>
              <ul className="space-y-4">
                <li className="flex items-center gap-3">
                  <i className="ri-star-fill text-yellow-400 text-2xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-xl"><strong className="text-blue-900">10% to giving</strong></span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-star-fill text-yellow-400 text-2xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-xl"><strong className="text-blue-900">10% to saving</strong></span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-star-fill text-yellow-400 text-2xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-xl"><strong className="text-blue-900">10% to investing</strong></span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-star-fill text-yellow-400 text-2xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-xl">My big savings goal: <strong className="text-blue-900">Trip to Iceland</strong></span>
                </li>
              </ul>
            </div>

            {/* Fun Facts Box */}
            <div className="bg-white rounded-3xl p-8 shadow-lg border-4 border-blue-200">
              <h3 className="text-3xl font-bold text-gray-800 mb-6" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                Fun Facts About Me
              </h3>
              <ul className="space-y-4">
                <li className="flex items-center gap-3">
                  <i className="ri-star-fill text-yellow-400 text-2xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-xl">Favorite Color: <strong className="text-blue-900">Dark Blue</strong></span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-star-fill text-yellow-400 text-2xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-xl">Favorite Sport: <strong className="text-blue-900">Soccer</strong></span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-star-fill text-yellow-400 text-2xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-xl">Superpower: <strong className="text-blue-900">Reading</strong></span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-star-fill text-yellow-400 text-2xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-xl">Dream: <strong className="text-blue-900">Travel the world</strong></span>
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Gallery Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-20"
          >
            <h2 className="text-4xl font-bold text-center text-gray-800 mb-12" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
              Wolfie in Action!
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="rounded-3xl overflow-hidden shadow-lg border-4 border-blue-200 h-64">
                <img
                  src="https://public.readdy.ai/ai/img_res/d9fee348f086b106e377d402e2612bb0.jpg"
                  alt="Wolfie cleaning"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="rounded-3xl overflow-hidden shadow-lg border-4 border-blue-200 h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=young%20boy%20in%20red%20cap%20giving%20thumbs%20up%20next%20to%20sparkling%20clean%20blue%20trash%20bin%2C%20big%20smile%20on%20face%2C%20suburban%20house%20background%2C%20bright%20sunny%20day%2C%20professional%20kid-friendly%20service%20aesthetic%20with%20primary%20colors%2C%20clean%20simple%20background%20showcasing%20achievement%20and%20pride%20in%20work&width=400&height=400&seq=wolfie2&orientation=squarish"
                  alt="Wolfie with clean bin"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="rounded-3xl overflow-hidden shadow-lg border-4 border-blue-200 h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=young%20boy%20wearing%20red%20cap%20and%20colorful%20t-shirt%20holding%20cleaning%20equipment%20and%20spray%20bottle%2C%20standing%20confidently%20in%20front%20of%20neighborhood%20street%2C%20energetic%20happy%20expression%2C%20professional%20cleaning%20crew%20photo%20with%20bright%20cheerful%20atmosphere%2C%20simple%20clean%20suburban%20background%20showing%20teamwork%20and%20dedication&width=400&height=400&seq=wolfie3&orientation=squarish"
                  alt="Wolfie with equipment"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
}